import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fundingAuthAPI, getIpeWorkspaceMode } from '@/utils/api';
import { isTeamSpaceEntryAvailable } from '@/pages/ipe/workbench/utils/accountCenterUtils';
import {
  buildTeamOwnerNameMap,
  loadAiCopywritingTemplatesForStudio,
} from '@/pages/ipe/workbench/utils/aiCopywritingTemplateLoader';
import {
  resolveDefaultTitlePresetScope,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import {
  appendCopywritingMaxCharsToPrompt,
  DEFAULT_AI_COPYWRITING_LANGUAGE,
  normalizeCopywritingMaxChars,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  createAndRunAiCopywritingTaskFromDetail,
  extractAiCopywritingTitlesFromTask,
} from '@/pages/ipe/workbench/utils/aiCopywritingDetailLaunch';
import { notifyQuickBridgeTaskCreated } from '@/pages/ipe/workbench/utils/showTaskCreatedDetailToast';
import { remakeAiCopywritingGroupInPlace } from '@/pages/ipe/workbench/utils/aiCopywritingRemakeInPlace';
import { resolveCopywritingPromptText } from '@/pages/ipe/workbench/utils/aiCopywritingDetailUtils';
import { waitForFundingTaskComplete } from '@/pages/ipe/workbench/utils/waitForFundingTask';
import { confirmIpeCreditAction } from '@/utils/ipeCreditConfirm';
import i18n from '@/i18n';

function collectSourceItemIds(sources = []) {
  const ids = [];
  (Array.isArray(sources) ? sources : []).forEach((source) => {
    (Array.isArray(source?.itemIds) ? source.itemIds : []).forEach((id) => {
      if (id != null && String(id).trim()) ids.push(String(id));
    });
  });
  return ids;
}

/**
 * 详情页快捷功能「AI文案」：打开文案模板选择弹窗 → 确认后创建任务；自定义模式走分组队列，默认模式走平铺生成。
 *
 * @param {{
 *   setToast?: (payload: { message: string, type?: string }) => void,
 *   onTaskCreated?: (taskId: string) => void,
 *   stayOnPage?: boolean,
 *   onStayOnPageTitle?: (payload: { source: object, title: string, task?: object, index?: number }) => Promise<void>|void,
 *   onStayOnPageGenerateStart?: (payload: { sources: object[], itemIds: string[] }) => void,
 *   onStayOnPageGenerateEnd?: (payload: { sources: object[], itemIds: string[] }) => void,
 *   onRemakeComplete?: (payload: { taskId: string, groupIndex: number, result: object, title: string }) => void,
 * }} [options]
 */
export default function useDetailAiCopywritingBridge({
  setToast,
  onTaskCreated,
  onRemakeComplete,
  stayOnPage = false,
  onStayOnPageTitle,
  onStayOnPageGenerateStart,
  onStayOnPageGenerateEnd,
} = {}) {
  const navigate = useNavigate();
  const workspaceMode = useIpeWorkspaceMode();
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [templateScope, setTemplateScope] = useState(() => (
    resolveDefaultTitlePresetScope(getIpeWorkspaceMode(), false)
  ));
  const [canAccessTeamPresets, setCanAccessTeamPresets] = useState(false);
  const [teamOwnerNameByUserId, setTeamOwnerNameByUserId] = useState({});
  const [pendingSources, setPendingSources] = useState([]);
  const [pendingSourceGoodsTaskId, setPendingSourceGoodsTaskId] = useState('');
  const [modalIntent, setModalIntent] = useState('create');
  const [targetTaskId, setTargetTaskId] = useState('');
  const [targetGroupIndex, setTargetGroupIndex] = useState(-1);
  const [initialLanguage, setInitialLanguage] = useState(DEFAULT_AI_COPYWRITING_LANGUAGE);
  const [initialMaxChars, setInitialMaxChars] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');

  const toast = useCallback((message, type = 'info') => {
    setToast?.({ message, type });
  }, [setToast]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const list = await loadAiCopywritingTemplatesForStudio();
        if (!cancelled) setPresets(list);
      } catch (error) {
        console.error('加载文案模板失败:', error);
      }
    };
    load();
    fundingAuthAPI.getAccountCenter()
      .then(async (center) => {
        if (cancelled) return;
        const available = isTeamSpaceEntryAvailable(center);
        setCanAccessTeamPresets(available);
        setTemplateScope(resolveDefaultTitlePresetScope(workspaceMode, available));
        if (!available) {
          setTeamOwnerNameByUserId({});
          return;
        }
        try {
          const overview = await fundingAuthAPI.getTeamOverview();
          if (!cancelled) {
            setTeamOwnerNameByUserId(buildTeamOwnerNameMap(overview?.members));
          }
        } catch (_) {
          if (!cancelled) setTeamOwnerNameByUserId({});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCanAccessTeamPresets(false);
          setTeamOwnerNameByUserId({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceMode]);

  const openAiCopywritingPicker = useCallback((sources, options = {}) => {
    const list = Array.isArray(sources)
      ? sources.filter((s) => s?.id && (s.url || s.previewUrl))
      : [];
    if (list.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return;
    }
    setPendingSources(list.map((s) => ({
      id: String(s.id),
      name: s.name || s.id,
      url: String(s.url || s.previewUrl || '').trim(),
      previewUrl: String(s.previewUrl || s.url || '').trim(),
      itemIds: Array.isArray(s.itemIds) ? s.itemIds : [],
    })));
    setPendingSourceGoodsTaskId(String(options.sourceGoodsTaskId || '').trim());
    setModalIntent('create');
    setTargetTaskId('');
    setTargetGroupIndex(-1);
    setInitialLanguage(DEFAULT_AI_COPYWRITING_LANGUAGE);
    setInitialMaxChars('');
    setInitialPrompt('');
    setSelectedPresetId('');
    setTemplateScope(resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets));
    setModalOpen(true);
  }, [toast, workspaceMode, canAccessTeamPresets]);

  const openAiCopywritingRemake = useCallback((task, groupIndex, source) => {
    const url = String(source?.url || source?.previewUrl || '').trim();
    if (!url || !task?.id) {
      toast(i18n.t('workbench:ipeUtilError.noImagesToRemake'), 'warning');
      return;
    }
    const result = task?.result && typeof task.result === 'object' ? task.result : {};
    setPendingSources([{
      id: String(source?.id || `ref-${groupIndex}`),
      name: source?.name || i18n.t('workbench:studioChrome.productImageNth', { index: Number(groupIndex) + 1 }),
      url,
      previewUrl: String(source?.previewUrl || url).trim(),
    }]);
    setPendingSourceGoodsTaskId('');
    setModalIntent('remake');
    setTargetTaskId(String(task.id));
    setTargetGroupIndex(Number(groupIndex));
    setInitialLanguage(String(result.language || DEFAULT_AI_COPYWRITING_LANGUAGE));
    const maxRaw = result.maxChars;
    setInitialMaxChars(
      maxRaw != null && Number.isFinite(Number(maxRaw)) && Number(maxRaw) > 0
        ? String(Math.floor(Number(maxRaw)))
        : '',
    );
    setInitialPrompt(resolveCopywritingPromptText(result));
    setSelectedPresetId(String(result.titlePresetId || ''));
    setTemplateScope(resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets));
    setModalOpen(true);
  }, [toast, workspaceMode, canAccessTeamPresets]);

  const closeAiCopywritingPicker = useCallback(() => {
    if (busy) return;
    setModalOpen(false);
    setPendingSources([]);
    setPendingSourceGoodsTaskId('');
    setSelectedPresetId('');
    setModalIntent('create');
    setTargetTaskId('');
    setTargetGroupIndex(-1);
    setInitialLanguage(DEFAULT_AI_COPYWRITING_LANGUAGE);
    setInitialMaxChars('');
    setInitialPrompt('');
  }, [busy]);

  const confirmAiCopywritingTemplate = useCallback(async (payload) => {
    if (busy) return;
    if (pendingSources.length === 0) {
      toast(i18n.t('workbench:ipeUtilError.pickImagesFirst'), 'warning');
      return;
    }
    const selectedPreset = selectedPresetId
      ? presets.find((p) => String(p.id) === String(selectedPresetId))
      : null;
    if (selectedPresetId && !selectedPreset) {
      toast(i18n.t('workbench:ipeUtilError.templateMissingReselect'), 'warning');
      return;
    }
    const language = String(payload?.language || DEFAULT_AI_COPYWRITING_LANGUAGE).trim()
      || DEFAULT_AI_COPYWRITING_LANGUAGE;
    const maxChars = normalizeCopywritingMaxChars(payload?.maxChars);
    const templateContent = String(selectedPreset?.content || '').trim();
    const stylePrompt = String(payload?.prompt || '').trim();
    const prompt = appendCopywritingMaxCharsToPrompt(stylePrompt || templateContent, maxChars);
    const uploadMode = String(payload?.uploadMode || 'default').trim().toLowerCase() === 'custom'
      ? 'custom'
      : 'default';

    const confirmed = await confirmIpeCreditAction({
      navId: 'aiCopywriting',
      imageCount: Math.max(1, pendingSources.length),
      title: i18n.t('workbench:nav.aiCopywriting.label', { defaultValue: 'AI 文案' }),
      confirmLabel: stayOnPage || modalIntent === 'remake'
        ? i18n.t('workbench:studioChrome.confirmGenerate', { defaultValue: '确认生成' })
        : i18n.t('workbench:studioChrome.confirmImport'),
    });
    if (!confirmed) return;

    const sourcesSnapshot = pendingSources.slice();
    const sourceGoodsTaskIdSnapshot = pendingSourceGoodsTaskId;
    const remakeIntent = modalIntent === 'remake' && targetTaskId && targetGroupIndex >= 0;
    const remakeTaskId = targetTaskId;
    const remakeGroupIndex = targetGroupIndex;
    const presetIdSnapshot = selectedPreset ? String(selectedPresetId) : '';
    const presetNameSnapshot = selectedPreset?.name || '';

    // 本页生成：立刻关弹窗，加载态交给表格标题列，不占用工具栏 busy
    if (stayOnPage || remakeIntent) {
      setModalOpen(false);
      setPendingSources([]);
      setPendingSourceGoodsTaskId('');
      setSelectedPresetId('');
      setModalIntent('create');
      setTargetTaskId('');
      setTargetGroupIndex(-1);
      setInitialLanguage(DEFAULT_AI_COPYWRITING_LANGUAGE);
      setInitialMaxChars('');
      setInitialPrompt('');

      const itemIds = collectSourceItemIds(sourcesSnapshot);
      onStayOnPageGenerateStart?.({ sources: sourcesSnapshot, itemIds });
      try {
        if (remakeIntent) {
          const remade = await remakeAiCopywritingGroupInPlace({
            taskId: remakeTaskId,
            groupIndex: remakeGroupIndex,
            prompt,
            language,
            maxChars,
            titlePresetId: presetIdSnapshot,
            titlePresetName: presetNameSnapshot,
            titlePresetContent: templateContent,
          });
          onRemakeComplete?.({
            taskId: remade.taskId,
            groupIndex: remade.groupIndex,
            result: remade.result,
            title: remade.title,
          });
          toast(i18n.t('workbench:ipeUtilError.titleRegenerated'), 'success');
          return;
        }

        const created = await createAndRunAiCopywritingTaskFromDetail({
          sources: sourcesSnapshot,
          prompt,
          language,
          maxChars,
          uploadMode,
          titlePresetId: presetIdSnapshot,
          titlePresetName: presetNameSnapshot,
          titlePresetContent: templateContent,
          sourceGoodsTaskId: sourceGoodsTaskIdSnapshot,
        });
        const taskId = String(created?.taskId || '').trim();
        onTaskCreated?.(taskId);
        const task = await waitForFundingTaskComplete(taskId);
        const titles = extractAiCopywritingTitlesFromTask(task);
        if (titles.length === 0) {
          throw new Error(i18n.t('workbench:ipeUtilError.copyGeneratedNoTitle'));
        }
        let applied = 0;
        let lastError = null;
        for (let index = 0; index < sourcesSnapshot.length; index += 1) {
          const source = sourcesSnapshot[index];
          const title = titles[index] || titles[0];
          if (!title) continue;
          try {
            await onStayOnPageTitle?.({ source, title, task, index });
            applied += 1;
          } catch (error) {
            lastError = error;
          }
        }
        if (applied === 0) {
          throw lastError || new Error('创建 AI 文案任务失败');
        }
        toast(i18n.t('workbench:ipeUtilError.copyAppliedCount', { count: applied }), 'success');
        if (lastError) {
          toast(
        lastError?.message || i18n.t('workbench:ipeUtilError.copyPartialFailed'),
        'warning',
      );
        }
      } catch (error) {
        console.error('创建 AI 文案任务失败', error);
        toast(
      error?.message || i18n.t('workbench:ipeUtilError.createAiCopyTaskFailed'),
      'error',
    );
      } finally {
        onStayOnPageGenerateEnd?.({ sources: sourcesSnapshot, itemIds });
      }
      return;
    }

    setBusy(true);
    try {
      const created = await createAndRunAiCopywritingTaskFromDetail({
        sources: sourcesSnapshot,
        prompt,
        language,
        maxChars,
        uploadMode,
        titlePresetId: presetIdSnapshot,
        titlePresetName: presetNameSnapshot,
        titlePresetContent: templateContent,
        sourceGoodsTaskId: sourceGoodsTaskIdSnapshot,
      });
      const taskId = String(created?.taskId || '').trim();
      onTaskCreated?.(taskId);
      notifyQuickBridgeTaskCreated({
        created: { taskId, navId: 'aiCopywriting' },
        fallbackNavId: 'aiCopywriting',
        navigate,
      });

      setModalOpen(false);
      setPendingSources([]);
      setPendingSourceGoodsTaskId('');
      setSelectedPresetId('');
    } catch (error) {
      console.error('创建 AI 文案任务失败', error);
      toast(
      error?.message || i18n.t('workbench:ipeUtilError.createAiCopyTaskFailed'),
      'error',
    );
    } finally {
      setBusy(false);
    }
  }, [
    busy,
    navigate,
    onRemakeComplete,
    onStayOnPageGenerateEnd,
    onStayOnPageGenerateStart,
    onStayOnPageTitle,
    onTaskCreated,
    pendingSourceGoodsTaskId,
    pendingSources,
    presets,
    selectedPresetId,
    stayOnPage,
    targetGroupIndex,
    targetTaskId,
    modalIntent,
    toast,
  ]);

  const goManageTemplates = useCallback((scope) => {
    setModalOpen(false);
    navigate('/ipe', {
      state: {
        activeNav: 'aiCopywriting',
        openCreate: true,
        openCopywritingTemplateLibrary: true,
        copywritingTemplateScope: scope || resolveDefaultTitlePresetScope(workspaceMode, canAccessTeamPresets),
      },
    });
  }, [navigate, workspaceMode, canAccessTeamPresets]);

  return {
    copywritingBusy: busy,
    copywritingModalOpen: modalOpen,
    copywritingModalIntent: modalIntent,
    copywritingInitialLanguage: initialLanguage,
    copywritingInitialMaxChars: initialMaxChars,
    copywritingInitialPrompt: initialPrompt,
    copywritingPresets: presets,
    copywritingSelectedPresetId: selectedPresetId,
    setCopywritingSelectedPresetId: setSelectedPresetId,
    copywritingTemplateScope: templateScope,
    copywritingCanAccessTeamPresets: canAccessTeamPresets,
    copywritingOwnerNameByUserId: teamOwnerNameByUserId,
    copywritingSourceCount: pendingSources.length,
    copywritingSources: pendingSources,
    openAiCopywritingPicker,
    openAiCopywritingRemake,
    closeAiCopywritingPicker,
    confirmAiCopywritingTemplate,
    goManageCopywritingTemplates: goManageTemplates,
  };
}
